/** Centralized query key factory for React Query cache management */

import type { BulkQueryParams, FilterParams, SortParams } from "../types";

/** Params for cursor-based infinite queries (excludes cursor/offset from cache key). */
export type CursorBulkKeyParams = SortParams &
  FilterParams & { limit?: number };

export const queryKeys = {
  keywords: {
    all: ["keywords"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.keywords.all, "list", filters] as const,
    detail: (keyword: string) =>
      [...queryKeys.keywords.all, "detail", keyword] as const,
    trends: (keywords: string[]) =>
      [...queryKeys.keywords.all, "trends", keywords] as const,
    stats: () => [...queryKeys.keywords.all, "stats"] as const,
    bulk: (params?: BulkQueryParams) =>
      [...queryKeys.keywords.all, "bulk", params] as const,
    cursorBulk: (params?: CursorBulkKeyParams) =>
      [...queryKeys.keywords.all, "cursor-bulk", params] as const,
    bulkMonthly: (params?: BulkQueryParams) =>
      [...queryKeys.keywords.all, "bulk-monthly", params] as const,
    bulkBids: (params?: BulkQueryParams) =>
      [...queryKeys.keywords.all, "bulk-bids", params] as const,
    bulkRankings: (params?: BulkQueryParams) =>
      [...queryKeys.keywords.all, "bulk-rankings", params] as const,
    cursorMonthly: (params?: CursorBulkKeyParams) =>
      [...queryKeys.keywords.all, "cursor-monthly", params] as const,
    cursorRankings: (params?: CursorBulkKeyParams) =>
      [...queryKeys.keywords.all, "cursor-rankings", params] as const,
  },

  search: {
    all: ["search"] as const,
    results: (searchType: string, keyword?: string) =>
      [...queryKeys.search.all, "results", searchType, keyword] as const,
    batch: (keyword: string, types?: string[]) =>
      [...queryKeys.search.all, "batch", keyword, types] as const,
    suggestions: (keyword: string) =>
      [...queryKeys.search.all, "suggestions", keyword] as const,
    bulkRows: (params?: BulkQueryParams) =>
      [...queryKeys.search.all, "bulk-rows", params] as const,
    cursorRows: (params?: CursorBulkKeyParams) =>
      [...queryKeys.search.all, "cursor-rows", params] as const,
  },

  channels: {
    all: ["channels"] as const,
    byType: (type: string) => [...queryKeys.channels.all, type] as const,
    places: () => [...queryKeys.channels.all, "places"] as const,
    bulk: (params?: BulkQueryParams) =>
      [...queryKeys.channels.all, "bulk", params] as const,
    cursorBulk: (params?: CursorBulkKeyParams) =>
      [...queryKeys.channels.all, "cursor-bulk", params] as const,
  },

  contents: {
    all: ["contents"] as const,
    byType: (type: string) => [...queryKeys.contents.all, type] as const,
    bulk: (params?: BulkQueryParams) =>
      [...queryKeys.contents.all, "bulk", params] as const,
    cursorBulk: (params?: CursorBulkKeyParams) =>
      [...queryKeys.contents.all, "cursor-bulk", params] as const,
  },

  scores: {
    all: ["scores"] as const,
    list: () => [...queryKeys.scores.all, "list"] as const,
    bulk: (params?: BulkQueryParams) =>
      [...queryKeys.scores.all, "bulk", params] as const,
  },

  searchMarketing: {
    all: ["search-marketing"] as const,
    bids: () => [...queryKeys.searchMarketing.all, "bids"] as const,
    bulkBids: (params?: BulkQueryParams) =>
      [...queryKeys.searchMarketing.all, "bulk-bids", params] as const,
    cursorBids: (params?: CursorBulkKeyParams) =>
      [...queryKeys.searchMarketing.all, "cursor-bids", params] as const,
  },

  trends: {
    all: ["trends"] as const,
    byKeywords: (keywords: string[]) =>
      [...queryKeys.trends.all, keywords] as const,
    bulkRows: (params?: BulkQueryParams) =>
      [...queryKeys.trends.all, "bulk-rows", params] as const,
  },

  billing: {
    all: ["billing"] as const,
    plans: () => [...queryKeys.billing.all, "plans"] as const,
  },

  contentClone: {
    all: ["content-clone"] as const,
    persona: (channelType: string, channelId: string) =>
      ["content-clone", "persona", channelType, channelId] as const,
    versions: (channelType: string, channelId: string) =>
      ["content-clone", "versions", channelType, channelId] as const,
  },

  /* ──────────────────────── AI Agent System ──────────────────────── */
  agent: {
    all: ["agent"] as const,
    // 세션
    sessions: () => [...queryKeys.agent.all, "sessions"] as const,
    session: (sessionId: string) =>
      [...queryKeys.agent.all, "sessions", sessionId] as const,
    messages: (sessionId: string) =>
      [...queryKeys.agent.all, "messages", sessionId] as const,
    // 에이전트 정의
    definitions: () => [...queryKeys.agent.all, "definitions"] as const,
    definition: (agentId: string) =>
      [...queryKeys.agent.all, "definitions", agentId] as const,
    // 사용량
    usage: () => [...queryKeys.agent.all, "usage"] as const,
    usagePeriod: (period: string) =>
      [...queryKeys.agent.all, "usage", period] as const,
    // 캐시 시딩 (위젯 데이터 → React Query 캐시)
    seeded: (source: string, payload?: unknown) =>
      [...queryKeys.agent.all, "seeded", source, payload] as const,
  },
} as const;

/**
 * agentKeys — agent 전용 팩토리 alias (가독성)
 */
export const agentKeys = queryKeys.agent;
